/**
 * Upgrade Page
 * Main page for subscription management and upgrades
 */

import { useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSubscription } from './hooks/useSubscription';
import {
  UpgradeHero,
  CurrentPlanCard,
  PricingGrid,
  UpgradeFAQ,
} from './components';

export default function Upgrade() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const upgradeTarget = searchParams.get('upgrade');

  const {
    subscription,
    currentTier,
    subscriptionSource,
    isLoading,
    isCheckoutLoading,
    error,
    handleUpgrade,
    handleManageSubscription,
  } = useSubscription();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <UpgradeHero />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16 relative z-10">
        {/* Error message */}
        {error && (
          <div className="mb-6 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 animate-fade-in">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Upgrade highlight (when coming from a specific upgrade link) */}
        {upgradeTarget && currentTier === 'free' && (
          <div className="mb-6 bg-primary-50 border border-primary-200 rounded-xl p-6 animate-fade-in">
            <h3 className="font-semibold text-primary-800">
              {t('upgrade.ready', `Ready to upgrade to ${upgradeTarget === 'pro_plus' ? 'Pro+' : 'Pro'}?`)}
            </h3>
            <p className="mt-1 text-sm text-primary-700">
              {t('upgrade.complete', 'Complete your upgrade below to unlock all features.')}
            </p>
          </div>
        )}

        {/* Current Plan Card */}
        <div className="mb-8">
          <CurrentPlanCard
            subscription={subscription}
            subscriptionSource={subscriptionSource}
            isLoading={isCheckoutLoading}
            onManageSubscription={handleManageSubscription}
          />
        </div>

        {/* Section Header */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {currentTier === 'free'
              ? t('upgrade.plans.upgrade', 'Upgrade Your Plan')
              : t('upgrade.plans.compare', 'Compare Plans')}
          </h2>
          <p className="mt-2 text-gray-600">
            {t('upgrade.plans.subtitle', 'Choose the plan that fits your needs')}
          </p>
        </div>

        {/* Pricing Grid */}
        {!isLoading && (
          <PricingGrid
            currentTier={currentTier}
            subscriptionSource={subscriptionSource}
            isLoading={isCheckoutLoading}
            onUpgrade={handleUpgrade}
          />
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-12">
          <UpgradeFAQ />
        </div>
      </div>
    </div>
  );
}
